<template>
  <div>
    <div v-if="holdPortfolioWithInstructions">
      <PortfolioInstructionsEditable
        :portfolio="holdPortfolioWithInstructions"
        :registryDevices="props.registryDevices"
        @save="handlePortfolioSave"
      />
    </div>

    <!-- Show editor for new hold portfolio if none exists -->
    <div v-else>
      <PortfolioInstructionsEditable
        :portfolio="newHoldPortfolio"
        :registryDevices="props.registryDevices"
        :startInEditMode="true"
        @save="handleNewPortfolioSave"
        @cancel="handleNewPortfolioCancel"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  createEmptyPortfolioInstructions,
  getPortfolioWithGuaranteedInstructions,
} from "@/composables/usePortfolioUtils";
import PortfolioInstructionsEditable from "@/dashboard/production_strategy/overview/PortfolioInstructionsEditable.vue";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import type {
  Company,
  PortfolioWithInstructions,
  RegistryDevice,
} from "@/types";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const productionStore = useProductionStrategyStore();

const props = defineProps<{
  company: Company;
  registryDevices: RegistryDevice[];
}>();

// Helper function to create a new empty Hold portfolio
const createEmptyHoldPortfolio = (): PortfolioWithInstructions => ({
  name: t("hold_strategy.hold_portfolio"),
  portfolio_type: "HOLD",
  is_default: false,
  company_id: props.company.id!,
  portfolio_instructions: createEmptyPortfolioInstructions(),
  custom_user_upload_id: null,
});

// New hold portfolio for creation
const newHoldPortfolio = ref<PortfolioWithInstructions>(
  createEmptyHoldPortfolio()
);

const defaultHold = computed(() => {
  return productionStore.portfolios.find(
    (p) => p.portfolio_type === "HOLD" && p.is_default
  );
});

const holdPortfolioWithInstructions = computed(() => {
  const portfolio = defaultHold.value;
  if (!portfolio) return null;
  // Convert readonly portfolio to mutable for compatibility
  const mutablePortfolio = JSON.parse(JSON.stringify(portfolio));
  return getPortfolioWithGuaranteedInstructions(mutablePortfolio);
});

const handlePortfolioSave = async (
  updatedPortfolio: PortfolioWithInstructions
) => {
  if (!props.company.id) return;

  try {
    const success = await productionStore.saveHoldPortfolio(
      props.company.id,
      updatedPortfolio
    );
    if (success) {
      globalSuccessHandler(
        t("portfolio_instructions.update_success_title"),
        t("portfolio_instructions.update_success")
      );
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

const handleNewPortfolioSave = async (
  updatedPortfolio: PortfolioWithInstructions
) => {
  if (!props.company.id) return;

  try {
    const success = await productionStore.saveHoldPortfolio(
      props.company.id,
      updatedPortfolio
    );
    if (success) {
      // Reset the new portfolio to default values
      newHoldPortfolio.value = createEmptyHoldPortfolio();
      globalSuccessHandler(
        t("portfolio_instructions.update_success_title"),
        t("portfolio_instructions.update_success")
      );
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

const handleNewPortfolioCancel = () => {
  // Reset the new portfolio to default values
  newHoldPortfolio.value = createEmptyHoldPortfolio();
};
</script>
